import { useTranslation } from 'react-i18next';
import {
  DocumentGrid,
  DocumentFilters,
  DocumentFiltersAdvanced,
  NavigationBreadcrumb,
  EmptyDocumentsState,
  DeleteConfirmModal,
} from '../components';
import {
  useDocuments,
  useDocumentDownload,
  useDocumentDelete,
  useDocumentExportZip,
} from '../hooks';
import { useDocumentsStore } from '../store';
import { Document } from '../types';
import { Archive, FolderDown } from 'lucide-react';
import { useCallback, useMemo } from 'react';
import { DocumentPreviewModal } from '../components/DocumentPreviewModal';
import { toast } from 'sonner';
import { Button } from '@/components/ui/Button';
import { useOrganisationsStore } from '@/store/organisations.store';
import { useOrganisationMembers } from '@/hooks/useOrganisations';
import { useProfile } from '@/hooks/useAuth';

export const DocumentsPage = () => {
  const { t } = useTranslation();
  const {
    searchQuery,
    selectedCategory,
    selectedTags,
    selectedDomainUuid,
    selectedFolderUuid,
    selectedEmployeeUuid,
    dateFrom,
    dateTo,
    currentPage,
    isPreviewModalOpen,
    isDeleteModalOpen,
    deleteDocument,
    openPreviewModal,
    openDeleteModal,
    closeDeleteModal,
  } = useDocumentsStore();

  // Build query params with hierarchical filters
  const queryParams = useMemo(
    () => ({
      page: currentPage,
      limit: 20,
      category: selectedCategory || undefined,
      tags: selectedTags.length > 0 ? selectedTags : undefined,
      search: searchQuery || undefined,
      sortBy: 'createdAt' as const,
      sortOrder: 'DESC' as const,
      // Hierarchical navigation filters
      domainUuid: selectedDomainUuid || undefined,
      folderUuid: selectedFolderUuid || undefined,
      employeeUuid: selectedEmployeeUuid || undefined,
      dateFrom: dateFrom || undefined,
      dateTo: dateTo || undefined,
      // Set context and contextUuid based on selection
      context: selectedEmployeeUuid
        ? ('employee' as const)
        : selectedFolderUuid
          ? ('folder' as const)
          : selectedDomainUuid
            ? ('domain' as const)
            : undefined,
      contextUuid:
        selectedEmployeeUuid ||
        selectedFolderUuid ||
        selectedDomainUuid ||
        undefined,
    }),
    [
      currentPage,
      dateFrom,
      dateTo,
      searchQuery,
      selectedCategory,
      selectedDomainUuid,
      selectedEmployeeUuid,
      selectedFolderUuid,
      selectedTags,
    ]
  );

  const { data, isLoading, error } = useDocuments(queryParams);

  // Organisation role check
  const { data: profileData } = useProfile();
  const profileUser = profileData?.data;
  const currentOrganisation = useOrganisationsStore(s => s.currentOrganisation);
  const { data: members } = useOrganisationMembers(
    currentOrganisation?.uuid || ''
  );
  const isOrgAdmin = useMemo(() => {
    if (!profileUser || !members) return false;
    const userUuid = profileUser.uuid || profileUser.id;
    const member = members.find(m => m.userUuid === userUuid);
    return member?.role === 'owner' || member?.role === 'admin';
  }, [profileUser, members]);

  // Hooks pour les actions sur les documents
  const { mutate: downloadDocument } = useDocumentDownload();
  const { mutate: deleteDocumentMutation } = useDocumentDelete();
  const { mutate: exportZip, isPending: isExporting } = useDocumentExportZip();

  const handlePreview = useCallback(
    (uuid: string) => {
      openPreviewModal(uuid);
    },
    [openPreviewModal]
  );

  const handleDownload = useCallback(
    (document: Document) => {
      console.log('handleDownload called with:', document);
      downloadDocument(document);
      console.log('downloadDocument mutation triggered');
    },
    [downloadDocument]
  );

  const handleDelete = useCallback(
    (uuid: string) => {
      console.log('handleDelete called with uuid:', uuid);
      // Trouver le document pour ouvrir le modal de confirmation
      const document = data?.data.find(doc => doc.uuid === uuid);
      if (document) {
        openDeleteModal(document);
      }
    },
    [openDeleteModal, data]
  );

  const handleConfirmDelete = useCallback(() => {
    if (deleteDocument) {
      console.log('User confirmed, calling deleteDocument');
      deleteDocumentMutation(deleteDocument.uuid);
      console.log('deleteDocument mutation triggered');
      closeDeleteModal();
    }
  }, [deleteDocument, deleteDocumentMutation, closeDeleteModal]);

  const handleExportZip = useCallback(() => {
    exportZip(queryParams);
  }, [exportZip, queryParams]);

  const handleEdit = useCallback((document: Document) => {
    // TODO: Ouvrir un modal d'édition des métadonnées
    toast.info("Fonctionnalité d'édition en cours de développement");
    console.log('Edit document:', document);
  }, []);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-amber-100">
            <Archive className="h-6 w-6 text-amber-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-neutral-900 sm:text-3xl">
              {t('documents.title', 'Documents')}
            </h1>
            <p className="text-base text-neutral-600 sm:text-lg">
              {t(
                'documents.description',
                'Gérez tous vos documents centralisés'
              )}
            </p>
          </div>
        </div>
        {isOrgAdmin && data && data.data.length > 0 && (
          <Button
            onClick={handleExportZip}
            variant="outline"
            disabled={isExporting}
          >
            <FolderDown className="mr-2 h-4 w-4" />
            {isExporting ? 'Export...' : 'Export ZIP'}
          </Button>
        )}
      </div>

      {/* Navigation Breadcrumb */}
      <NavigationBreadcrumb />

      <div className="rounded-2xl border border-neutral-200 bg-white p-6">
        <div>
          {/* Advanced Filters */}
          <DocumentFiltersAdvanced />

          {/* Basic Filters */}
          <DocumentFilters />

          {/* Content */}
          {isLoading ? (
            <div className="py-12 text-center text-neutral-500">
              {t('common.loading', 'Chargement...')}
            </div>
          ) : error ? (
            <div className="bg-error-50 text-error-800 rounded-lg p-4">
              {t(
                'documents.errors.loadFailed',
                'Erreur lors du chargement des documents'
              )}
            </div>
          ) : !data || data.data.length === 0 ? (
            <EmptyDocumentsState />
          ) : (
            <>
              <DocumentGrid
                documents={data.data}
                onPreview={handlePreview}
                onDownload={handleDownload}
                onDelete={handleDelete}
                onEdit={handleEdit}
              />

              {/* Pagination */}
              {data.totalPages > 1 && (
                <div className="mt-6 flex items-center justify-between">
                  <p className="text-sm text-neutral-600">
                    {t('common.pagination', 'Page {{current}} sur {{total}}', {
                      current: data.page,
                      total: data.totalPages,
                    })}{' '}
                    ({data.total} {t('documents.title', 'documents')})
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Modals */}
      {isPreviewModalOpen && <DocumentPreviewModal />}
      {isDeleteModalOpen && deleteDocument && (
        <DeleteConfirmModal
          document={deleteDocument}
          onConfirm={handleConfirmDelete}
          onCancel={closeDeleteModal}
        />
      )}
    </div>
  );
};
